import { Component, computed, input, signal, viewChild } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { DateTime } from 'luxon'
import { DateTimePickerDirective } from '../date-time-picker.directive'
import { PickerMode } from '../date-time-picker.types'

const FORMATS: Record<PickerMode, string> = {
  date: 'd. M. yyyy',
  datetime: 'd. M. yyyy HH:mm',
  time: 'HH:mm',
}

const FORMATS_WITHOUT_YEAR: Record<PickerMode, string> = {
  date: 'd. M.',
  datetime: 'd. M. HH:mm',
  time: 'HH:mm',
}

/**
 * A date, date-and-time or time form field: the M3 outline field, read-only, with `[seiriDateTimePicker]` on its
 * suffix button. Clicking the field opens the picker too. `<seiri-date-time-field mode="date" label="Termín"
 * [formField]="form.date" />`.
 *
 * It has no width of its own: like a `mat-form-field`, its layout sizes it. `[showYear]="false"` leaves the year out
 * where two fields share a narrow row; the picker still shows it.
 *
 * Zone-free: its value is a browser-local wall-clock `Date`, shown and picked as such. A premise's form converts in its
 * mapper (`getUTCintoTimezone` in, `getDateTimeInTimezone` out). The value it was given is never mutated.
 *
 * A `ControlValueAccessor` rather than a `FormValueControl`, because `requested-absence` still binds it with reactive
 * `formControlName`; Signal Forms bind it through `[formField]` all the same. The control is touched when the picker
 * closes.
 */
@Component({
  selector: 'seiri-date-time-field',
  imports: [DateTimePickerDirective, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: DateTimeFieldComponent, multi: true }],
  templateUrl: './date-time-field.component.html',
  styleUrl: './date-time-field.component.css',
  host: {
    class: 'seiri-date-time-field',
  },
})
export class DateTimeFieldComponent implements ControlValueAccessor {
  readonly mode = input<PickerMode>('datetime')
  readonly label = input('')
  readonly showYear = input(true)
  readonly min = input<Date | null | undefined>(null)
  readonly max = input<Date | null | undefined>(null)
  readonly startAt = input<Date | null | undefined>(null)
  readonly startView = input<'days' | 'years'>('days')

  protected readonly value = signal<Date | null>(null)
  protected readonly disabled = signal(false)
  protected readonly text = computed(() => {
    const value = this.value()
    const format = (this.showYear() ? FORMATS : FORMATS_WITHOUT_YEAR)[this.mode()]
    return value ? DateTime.fromJSDate(value).toFormat(format) : ''
  })
  protected readonly icon = computed(() => (this.mode() === 'time' ? 'schedule' : 'calendar_month'))
  protected readonly triggerLabel = computed(() => (this.mode() === 'time' ? 'Vybrať čas' : 'Vybrať dátum'))

  private readonly picker = viewChild(DateTimePickerDirective)
  private onChange: (value: Date) => void = () => undefined
  private onTouched: () => void = () => undefined

  writeValue(value: Date | string | null | undefined): void {
    this.value.set(value ? new Date(value) : null)
  }

  registerOnChange(fn: (value: Date) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled)
  }

  protected open(): void {
    this.picker()?.open()
  }

  protected pick(date: Date): void {
    this.value.set(date)
    this.onChange(date)
  }

  protected touch(): void {
    this.onTouched()
  }
}
